import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { BadgeTypeConstants, ColorConstants, ProjectIssuanceConstants } from "../../../../../constants";
import { BadgeData } from "../../../../../models";
import { logError } from "../../../../../service/error";
import {
  GetProjectDetailsResponse,
  getProjectIssuances,
  getStandardSpecificSchemaDetails,
} from "../../../../../service/publicquery";
import { Status } from "../../../../../service/Shared";
import { getRestorationUnitTotals, jsonReviver, RestorationUnitTotals } from "../../../../../utils";
import { StackedChartData } from "../../../../../widget";
import { StandardSpecificDefinition } from "../../../../shared/projects/project/overview/components/StandardSpecificSection/types";
import { usePublicContext } from "../../../usePublicContext";

interface UsePreviewReturnData {
  isLoading: boolean;
  projectDetails: GetProjectDetailsResponse | undefined;
  tags: string[] | null;
  badges: BadgeData[];
  certifications: BadgeData[];
  stackedChartData: StackedChartData[] | undefined;
  xAxisLabels: string[];
  standardSpecificDefinition: StandardSpecificDefinition | undefined;
  restorationUnits: RestorationUnitTotals | undefined;
}

export const usePreview = (): UsePreviewReturnData => {
  const { uuid } = useParams();
  const { projectDetails } = usePublicContext();
  const [isLoading, setIsLoading] = useState(true);
  const [standardSpecificDefinition, setStandardSpecificDefinition] = useState<
    StandardSpecificDefinition | undefined
  >();

  const [stackedChartData, setStackedChartData] = useState<StackedChartData[]>([]);
  const [xAxisLabels, setXAxisLabels] = useState<string[]>([]);
  const [restorationUnits, setRestorationUnits] = useState<RestorationUnitTotals | undefined>();

  const tags = !!projectDetails?.tags && !!projectDetails?.tags.length ? projectDetails?.tags : null;
  const certifications =
    projectDetails?.badges.filter((b) => b.type === BadgeTypeConstants.BADGE_TYPE_CERTIFICATION) || [];
  const badges = projectDetails?.badges.filter((b) => b.type === BadgeTypeConstants.BADGE_TYPE_BADGE) || [];

  /*
    `isCurrent` reports whether this load is still the latest one, so a slower response for a project
    the preview has moved away from cannot overwrite the current one's state.
  */
  const fetchData = useCallback(async (projectUuid: string, isCurrent: () => boolean) => {
    const response = await getProjectIssuances({
      projectUuid,
      paging: {
        afterCursor: null,
        beforeCursor: null,
        limit: 30,
      },
    });

    if (!isCurrent()) return;

    if (response.status !== Status.Success || !response.data) {
      // As on the overview: the units tile stays hidden, but the failure is logged rather than passing
      // for a project with no units. Not toasted - this is a public page.
      logError({
        error: `Failed to load issuances for the public preview of project ${projectUuid}: ${
          response.errors?.map((error) => error.message).join("; ") || "no data returned"
        }`,
      });
    }

    if (response.status === Status.Success && response.data) {
      // KAN-9935 - as on the public overview: summed for every measurement, shown for Restoration only.
      setRestorationUnits(getRestorationUnitTotals(response.data.results));

      const keys: string[] = [];
      const verifiedUnits: number[] = [];
      const pendingUnits: number[] = [];

      const results = response.data?.results ?? [];

      results.forEach((value) => {
        const endYear = JSON.parse(value.standardSpecificIssuanceData, jsonReviver)?.vintageEndDate?.getFullYear();
        const period = endYear?.toString();

        // Avoid duplicate keys
        if (!keys.includes(period)) {
          const total = results
            .filter(
              (d) => JSON.parse(d.standardSpecificIssuanceData, jsonReviver)?.vintageEndDate?.getFullYear() <= endYear
            )
            .reduce((acc: number, k) => acc + k.unitQuantityTotal, 0);

          keys.push(period);
          verifiedUnits.push(total);
          pendingUnits.push(projectDetails?.cachedTotalQuantity ?? 0 - total);
        }
      });

      const unitTypes =
        projectDetails?.standard.uuid !== undefined
          ? ProjectIssuanceConstants.ISSUANCE_QUANTITY_TYPE_BY_STANDARD[projectDetails?.standard.uuid]
          : undefined;

      setXAxisLabels(keys);
      setStackedChartData([
        {
          label:
            (unitTypes !== undefined
              ? ProjectIssuanceConstants.ISSUANCE_QUANTITY_TYPE_LONG_HAND[unitTypes[0]]
              : undefined) ?? "Verified carbon units (VCUs)",
          data: verifiedUnits,
          backgroundColor: ColorConstants.INDIGO_500,
          hoverBackgroundColor: ColorConstants.INDIGO_400,
        },
        {
          label:
            (unitTypes !== undefined
              ? ProjectIssuanceConstants.ISSUANCE_QUANTITY_TYPE_LONG_HAND[unitTypes[1]]
              : undefined) ?? "Pending issuance units (PIUs)",
          data: pendingUnits,
          backgroundColor: ColorConstants.INDIGO_200,
          hoverBackgroundColor: ColorConstants.INDIGO_100,
        },
      ]);
    }

    if (projectDetails && projectDetails.standardSpecificSchemaUuid) {
      const standardDefinitionResponse = await getStandardSpecificSchemaDetails({
        standardSpecificSchemaUuid: projectDetails.standardSpecificSchemaUuid,
      });

      if (!isCurrent()) return;

      if (standardDefinitionResponse.status === Status.Success && standardDefinitionResponse.data) {
        setStandardSpecificDefinition(standardDefinitionResponse.data.definition);
      }
    }
  }, []);

  useEffect(() => {
    let isCurrent = true;

    if (uuid) {
      // Never show the previous project's totals while this one loads, or if its request fails.
      setRestorationUnits(undefined);

      fetchData(uuid, () => isCurrent).finally(() => {
        if (isCurrent) setIsLoading(false);
      });
    }

    return () => {
      isCurrent = false;
    };
  }, [fetchData, uuid]);

  return {
    isLoading,
    stackedChartData,
    xAxisLabels,
    tags,
    badges,
    certifications,
    projectDetails,
    standardSpecificDefinition,
    restorationUnits,
  };
};
